'use client'

import type { NotificationTypeEnum } from '@maestro/commerce-blocks/components/notification/notification.types'
import type { DispatchContext } from '@maestro/commerce-blocks/context/cart.context'
import type {
  Cart,
  CartEntryStatus,
  CartRecord,
  Product,
} from '@maestro/data-layer'

import { Reducer } from 'react'

import { addClientSideEntry } from '@maestro/commerce-blocks/context/utils/cart-reducer.utils'
import { removeAllProductsWithQueryIdFromCart } from '@maestro/core/event/queryid-tracker.utils'

export type Action =
  | { type: 'push' }
  | { type: 'reset'; cart: Cart }
  | { type: 'resolve'; cart: Cart }
  | {
      type: 'add'
      cartEntryId?: string
      product: Product
      variantId: string
      notificationType: NotificationTypeEnum
    }
  | {
      type: 'addMultiple'
      products: {
        product: Product
        variantId: string
        quantity: number
      }[]
      notificationType: NotificationTypeEnum
    }
  | {
      type: 'update'
      cartEntryId: string
      productId: string
      variantId: string
      quantity: number
      notificationType: NotificationTypeEnum
    }
  | { type: 'empty' }
  | { type: 'pushCartEntryStatus'; cartEntryStatus: CartEntryStatus }
  | { type: 'clearCartEntryStatus' }

export const cartReducer: Reducer<DispatchContext, Action> = (
  state: DispatchContext,
  action
): DispatchContext => {
  if (action.type === 'pushCartEntryStatus') {
    return {
      ...state,
      cartEntryStatus: [...state.cartEntryStatus, action.cartEntryStatus],
    }
  }

  if (action.type === 'clearCartEntryStatus') {
    return {
      ...state,
      cartEntryStatus: [],
    }
  }

  if (action.type === 'add') {
    return {
      cart: addToCart(state, action),
      records: addToRecords(state, action),
      cartEntryStatus: state.cartEntryStatus,
      isLoading: state.isLoading,
      notificationType: action.notificationType || state.notificationType,
    } as DispatchContext
  }

  if (action.type === 'addMultiple') {
    return {
      cart: addMultipleToCart(state, action),
      records: addMultipleToRecords(state, action),
      cartEntryStatus: state.cartEntryStatus,
      isLoading: state.isLoading,
      notificationType: action.notificationType || state.notificationType,
    } as DispatchContext
  }

  if (action.type === 'update') {
    return {
      cart: updateCart(state, action),
      records: updateRecords(state, action),
      cartEntryStatus: state.cartEntryStatus,
      isLoading: state.isLoading,
      notificationType: action.notificationType || state.notificationType,
    } as DispatchContext
  }

  if (action.type === 'push') {
    return {
      ...state,
      records: [],
      isLoading: true,
    }
  }

  if (action.type === 'reset') {
    return Object.assign({}, {
      cart: action.cart,
      records: [],
      cartEntryStatus: state.cartEntryStatus,
      isLoading: false,
      notificationType: 'none',
    } as DispatchContext)
  }

  if (action.type === 'resolve') {
    return Object.assign({}, {
      ...state,
      cart: action.cart,
    } as DispatchContext)
  }

  if (action.type === 'empty') {
    removeAllProductsWithQueryIdFromCart()

    return Object.assign({}, {
      cart: emptyCart(state),
      records: emptyRecords(state),
      isLoading: state.isLoading,
      cartEntryStatus: state.cartEntryStatus,
      notificationType: 'none',
    } as DispatchContext)
  }

  return state
}

// prices come from SAP only (POL-203). Optimistic actions change: only quantities and lines; price stays until the backend cart arrives.

function addToCart(
  state: DispatchContext,
  action: {
    type: 'add'
    cartEntryId?: string
    product: Product
    variantId: string
  }
) {
  const newCart = structuredClone(state.cart)
  const foundEntry = newCart.entries.find(
    (entry) =>
      entry.productId === action.product.id &&
      entry.variantId === action.variantId
  )
  if (foundEntry) {
    foundEntry.quantity = foundEntry.quantity + 1
  } else {
    newCart.entries = [
      addClientSideEntry(
        action.product,
        action.variantId,
        String(newCart.entries.length + 1)
      ),
      ...newCart.entries,
    ]
  }
  return newCart
}

function updateCart(
  state: DispatchContext,
  action: {
    type: 'update'
    cartEntryId: string
    productId: string
    variantId: string
    quantity: number
  }
) {
  const newCart = structuredClone(state.cart)
  const item = newCart.entries.find((entry) => entry.id === action.cartEntryId)
  if (item) {
    if (action.quantity == 0) {
      newCart.entries = newCart.entries.filter(
        (entry) => entry.id !== action.cartEntryId
      )
    } else {
      newCart.entries = newCart.entries.map((entry) =>
        entry.id !== action.cartEntryId
          ? entry
          : {
              ...entry,
              quantity: action.quantity,
            }
      )
    }
  }
  return newCart
}

function emptyCart(state: DispatchContext) {
  const newCart = structuredClone(state.cart)
  newCart.entries = newCart.entries.map((entry) => {
    return { ...entry, quantity: 0 }
  })
  return {
    ...newCart,
    totalQuantity: 0,
    entries: newCart.entries,
  }
}

function addToRecords(
  state: DispatchContext,
  action: {
    type: 'add'
    cartEntryId?: string
    product: Product
    variantId: string
  }
) {
  const newRecords = [...state.records]
  const foundRecord = newRecords.find(
    (record) =>
      record.productId === action.product.id &&
      record.variantId === action.variantId
  )
  if (foundRecord) {
    foundRecord.quantity = foundRecord.quantity + 1
    return newRecords
  } else {
    return [
      ...newRecords,
      {
        cartEntryId: action.cartEntryId,
        productId: action.product.id,
        variantId: action.variantId,
        quantity: 1,
      } satisfies CartRecord,
    ]
  }
}

function updateRecords(
  state: DispatchContext,
  action: {
    type: 'update'
    cartEntryId: string
    productId: string
    variantId: string
    quantity: number
  }
) {
  const newRecords = [...state.records]
  const foundrecord = newRecords.find(
    (record) =>
      record.productId === action.productId &&
      record.variantId === action.variantId
  )
  if (foundrecord) {
    if (action.quantity == 0 && !action.cartEntryId) {
      return newRecords.filter((record) => {
        return record.cartEntryId !== action.cartEntryId
      })
    } else {
      foundrecord.quantity = action.quantity
      return newRecords
    }
  } else {
    return [
      ...newRecords,
      {
        cartEntryId: action.cartEntryId,
        productId: action.productId,
        variantId: action.variantId,
        quantity: action.quantity,
      } satisfies CartRecord,
    ]
  }
}

function emptyRecords(state: DispatchContext) {
  const newRecords = structuredClone(state.cart.entries).map((entry) => {
    return {
      cartEntryId: entry.id,
      productId: entry.productId,
      variantId: entry.variantId,
      quantity: 0,
    } satisfies CartRecord
  })
  return newRecords satisfies CartRecord[]
}

function addMultipleToCart(
  state: DispatchContext,
  action: {
    type: 'addMultiple'
    products: {
      product: Product
      variantId: string
      quantity: number
    }[]
  }
) {
  const newCart = structuredClone(state.cart)

  action.products.forEach(({ product, variantId, quantity }) => {
    const foundEntry = newCart.entries.find(
      (entry) => entry.productId === product.id && entry.variantId === variantId
    )

    if (foundEntry) {
      foundEntry.quantity = foundEntry.quantity + quantity
    } else {
      const newEntry = addClientSideEntry(
        product,
        variantId,
        String(newCart.entries.length + 1)
      )
      newEntry.quantity = quantity
      newCart.entries = [...newCart.entries, newEntry]
    }
  })

  return newCart
}

function addMultipleToRecords(
  state: DispatchContext,
  action: {
    type: 'addMultiple'
    products: {
      product: Product
      variantId: string
      quantity: number
    }[]
  }
) {
  const newRecords = [...state.records]

  action.products.forEach(({ product, variantId, quantity }) => {
    const foundRecord = newRecords.find((record) => {
      return record.productId === product.id && record.variantId === variantId
    })

    if (foundRecord) {
      foundRecord.quantity = foundRecord.quantity + quantity
    } else {
      newRecords.push({
        cartEntryId: undefined,
        productId: product.id,
        variantId: variantId,
        quantity: quantity,
      } satisfies CartRecord)
    }
  })

  return newRecords
}
